import { Component } from '@theme/component';

const STORAGE_KEY = 'smartify-appearance-v1';
const CHANGE_EVENT = 'smartify:appearance-change';

/**
 * Keeps all header controls in sync with the document's prepaint appearance.
 * @typedef {object} AppearanceRefs
 * @property {HTMLButtonElement} button - Native toggle button.
 * @extends {Component<AppearanceRefs>}
 */
class SmartifyAppearance extends Component {
  requiredRefs = ['button'];
  #listeners;
  #media;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    document.addEventListener(CHANGE_EVENT, this.#sync, options);
    window.addEventListener('storage', this.#onStorage, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    try {
      this.#media = window.matchMedia('(prefers-color-scheme: dark)');
      this.#media.addEventListener('change', this.#followSystem, options);
    } catch (error) {}
    this.#followSystem();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** Switches appearance; storage failure must not disable the current-page control. */
  handleToggle() {
    const root = document.documentElement;
    const mode = root.dataset.smartifyMode === 'dark' ? 'light' : 'dark';
    root.dataset.smartifyInteracted = 'true';
    root.dataset.smartifyPreference = mode;
    root.dataset.smartifyMode = mode;
    // A failed write still represents a deliberate choice for this page's lifetime.
    root.dataset.smartifyUnpersisted = 'true';
    try {
      localStorage.setItem(STORAGE_KEY, mode);
      root.dataset.smartifyUnpersisted = 'false';
    } catch (error) {}
    document.dispatchEvent(new Event(CHANGE_EVENT));
  }

  #sync = () => {
    this.refs.button.setAttribute('aria-pressed', String(document.documentElement.dataset.smartifyMode === 'dark'));
  };

  #followSystem = () => {
    const root = document.documentElement;
    if (root.dataset.smartifyInteracted === 'true' || ['light', 'dark'].includes(root.dataset.smartifyPreference) || !this.#media) return;
    root.dataset.smartifyMode = this.#media.matches ? 'dark' : 'light';
    document.dispatchEvent(new Event(CHANGE_EVENT));
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    const root = document.documentElement;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (['light', 'dark'].includes(saved)) {
        root.dataset.smartifyPreference = saved;
        root.dataset.smartifyMode = saved;
        root.dataset.smartifyUnpersisted = 'false';
        document.dispatchEvent(new Event(CHANGE_EVENT));
        return;
      }
      if (root.dataset.smartifyUnpersisted !== 'true') {
        root.dataset.smartifyPreference = 'system';
        root.dataset.smartifyInteracted = 'false';
      }
    } catch (error) {}
    this.#followSystem();
  };

  /** @param {StorageEvent} event */
  #onStorage = (event) => {
    if (event.key !== STORAGE_KEY && event.key !== null) return;
    try {
      if (event.storageArea !== localStorage) return;
    } catch (error) {
      return;
    }
    const root = document.documentElement;
    if (event.newValue === null) {
      if (root.dataset.smartifyUnpersisted === 'true') return;
      root.dataset.smartifyPreference = 'system';
      root.dataset.smartifyInteracted = 'false';
      this.#followSystem();
      return;
    }
    if (!['light', 'dark'].includes(event.newValue)) return;
    root.dataset.smartifyPreference = event.newValue;
    root.dataset.smartifyMode = event.newValue;
    root.dataset.smartifyUnpersisted = 'false';
    document.dispatchEvent(new Event(CHANGE_EVENT));
  };
}

if (!customElements.get('smartify-appearance')) {
  customElements.define('smartify-appearance', SmartifyAppearance);
}
